import { useId, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Link,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import RuleInstances from '@components/findings/RuleInstances';
import { RuleTitleInTemplate } from '@components/findings/RuleTitle';
import { stripCodeSpans } from '@utils/codeSpans';
import type { TestResultItem } from '@services/testService';
import { findingsFromScanResults } from '@utils/scanRunFindings';
import { contentCategoryLabel } from '@utils/contentCategoryLabels';
import {
  filterGroups,
  type CategoryGroup,
  type GroupSeverity,
  type RuleGroup,
} from '@utils/scanRunGroups';

/**
 * REV-720: the findings a scan run produced, in the same table `/findings`
 * uses.
 *
 * This replaces a second implementation of that list — a per-target table with
 * its own columns, its own severity labels and its own idea of what a finding
 * is. The walkthrough's complaint was that the two disagreed; the fix is that
 * there is only one of them.
 *
 * There are still two SOURCES, and the reason is structural rather than
 * incidental:
 *
 *   - A run against a tracked place persists a `FindingInstance` per issue, so
 *     `/findings?scan_run_id=` answers for it. Rows link to finding detail and
 *     carry everything the org-wide list carries.
 *   - A run against an untracked URL persists none. `testExecutionEngine` skips
 *     them when there is no context, because every piece of durable evidence
 *     carries a `context_id` — so the endpoint cannot answer, by design. Those
 *     results live only in the Mongo `TestResult`, for 24 hours.
 *
 * Asking the endpoint anyway for the second case would render an empty table
 * and present it as the answer. So the Mongo rows are adapted into the same
 * shape and rendered by the same component, with no detail link and no
 * per-finding actions — there is no persisted finding to open or disposition —
 * and a prompt explaining what registering the place would change.
 */
/**
 * REV-715: and grouped the way AFixt-Investigator groups them — by content
 * type, then by the rule that failed, then its instances — because a flat list
 * of every instance was, in the walkthrough's words, "vomiting information".
 *
 * Every level starts collapsed. A content type and a rule are each a heading
 * (h3, h4 under this section's h2) holding a native button with aria-expanded;
 * the counts are part of the button's text, so they are announced with it. The
 * grouping comes from the run's aggregates (`byCategory`), because the
 * instances are paged and one page of them cannot be grouped. The instances of
 * a rule load only when it is opened (RuleInstances).
 */
export interface ScanRunFindingsProps {
  scanRunId: string;
  /** Null for a run against an untracked URL. That is the whole distinction. */
  contextId?: string | null;
  organizationId?: string;
  /**
   * REV-949: whether the caller's role may change a finding's status
   * (canTriageFindings). False for a viewer, who then gets no bulk triage.
   */
  canTriage: boolean;
  orgSlug?: string;
  /** Mongo-backed per-target results. Used only when there is no context. */
  results: TestResultItem[] | null;
  /**
   * REV-715: the run's findings grouped by content category, then rule, from
   * the run's aggregates (`aggregates.byCategory.categories`).
   */
  groups?: readonly CategoryGroup[];
}

const SEVERITY_OPTIONS: readonly GroupSeverity[] = ['critical', 'major', 'minor', 'info'];

/** Stands in for the rule's title in a translated string; no title contains it. */
const RULE_SLOT = '\u0000rule\u0000';

export default function ScanRunFindings({
  scanRunId,
  contextId,
  organizationId,
  canTriage,
  orgSlug,
  results,
  groups = [],
}: ScanRunFindingsProps) {
  const { t } = useTranslation();
  const baseId = useId();

  /**
   * Two different questions, and conflating them made the table hang.
   *
   *   - `unregistered` is about the RUN: no tracked place, so nothing was
   *     persisted and nothing will be compared. It drives the prompt.
   *   - `usesEndpoint` is about whether this render can READ the endpoint,
   *     which also needs an organization. The page passes `activeOrg?.id`, and
   *     a scan run is viewable with no active org selected — so that is a real
   *     combination, not a defensive one.
   *
   * When they disagree the Mongo results are still in the page's state and are
   * the same findings, just without ids to link to. Rendering those beats a
   * spinner that never resolves, which is what one flag produced.
   */
  const unregistered = !contextId;
  const usesEndpoint = !!contextId && !!organizationId;

  const ephemeral = useMemo(() => findingsFromScanResults(results), [results]);

  // REV-712's filters, applied to the grouping: every count follows them.
  const [severityFilter, setSeverityFilter] = useState<GroupSeverity | ''>('');
  const [ruleFilter, setRuleFilter] = useState('');
  const shown = useMemo(
    () =>
      filterGroups(groups, {
        severity: severityFilter || undefined,
        ruleId: ruleFilter || undefined,
      }),
    [groups, severityFilter, ruleFilter]
  );
  const ruleOptions = useMemo(
    () => groups.flatMap((g) => g.rules.map((r) => r.ruleId)).sort(),
    [groups]
  );

  // Collapsed by default, at every level (REV-715 decision, 2026-09-29).
  const [openCategories, setOpenCategories] = useState<ReadonlySet<string>>(new Set());
  const [openRules, setOpenRules] = useState<ReadonlySet<string>>(new Set());
  const toggle =
    (setter: typeof setOpenCategories) =>
    (key: string): void =>
      setter((cur) => {
        const next = new Set(cur);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
      });
  const toggleCategory = toggle(setOpenCategories);
  const toggleRule = toggle(setOpenRules);

  // Ids are derived from list position, not from catalog values, which carry
  // spaces and punctuation ("Images and other non-text content").
  const categoryIds = (i: number) => ({
    heading: `${baseId}-cat-${i}`,
    panel: `${baseId}-cat-${i}-panel`,
  });
  const ruleIds = (i: number, j: number) => ({
    heading: `${baseId}-cat-${i}-rule-${j}`,
    panel: `${baseId}-cat-${i}-rule-${j}-panel`,
  });

  const ruleLabel = (rule: RuleGroup) =>
    rule.title ? `${rule.title} (${rule.ruleId})` : rule.ruleId;

  return (
    <Paper variant="outlined" sx={{ p: 3, mb: 2 }}>
      <Typography
        id="scan-findings-heading"
        variant="h2"
        sx={{ fontSize: '1.25rem', fontWeight: 600, mb: 2 }}
      >
        {t('scanFindings.heading', 'Issues found')}
      </Typography>

      {/* Not gated on finding anything. Someone who scans an untracked URL and
          gets a clean result is the person most likely to leave believing the
          site is fine AND being watched; neither is true, and the empty table
          says nothing about either. */}
      {unregistered && (
        <Alert
          severity="info"
          /* MUI defaults an Alert to role="alert", an ASSERTIVE live region
             that interrupts a screen reader. This text is present at first
             render and answers no action, so it is not a live region at all. */
          role="note"
          sx={{ mb: 2 }}
        >
          <AlertTitle>
            {t('scanFindings.unregistered.title', 'These results are temporary')}
          </AlertTitle>
          {/* The engine persists no finding for a run with no tracked place, so
              there is nothing here to open, assign, or mark a false positive —
              and nothing to compare the next scan against. Say that, rather
              than showing controls that would refuse. */}
          {t(
            'scanFindings.unregistered.body',
            'This scan ran against a URL that is not tracked, so its findings are not saved and expire after 24 hours. They cannot be opened, assigned, or triaged, nothing will compare them to the next scan, and their WCAG criteria are not resolved — which is why that column is empty.'
          )}{' '}
          <Link component={RouterLink} to="/contexts/new">
            {t('scanFindings.unregistered.cta', 'Track this place to keep its findings')}
          </Link>
        </Alert>
      )}

      {/* REV-712: filters. Labelled selects, so each is announced by name. */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <TextField
          select
          size="small"
          label={t('scanFindings.filters.severity', 'Severity')}
          value={severityFilter}
          onChange={(e) => setSeverityFilter(e.target.value as GroupSeverity | '')}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">{t('scanFindings.filters.all', 'All')}</MenuItem>
          {SEVERITY_OPTIONS.map((sev) => (
            <MenuItem key={sev} value={sev}>
              {t(`findings.severities.${sev}`, sev.charAt(0).toUpperCase() + sev.slice(1))}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label={t('scanFindings.filters.rule', 'Rule')}
          value={ruleFilter}
          onChange={(e) => setRuleFilter(e.target.value)}
          sx={{ minWidth: 220 }}
        >
          <MenuItem value="">{t('scanFindings.filters.all', 'All')}</MenuItem>
          {ruleOptions.map((rule) => (
            <MenuItem key={rule} value={rule}>
              {rule}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      {shown.length === 0 && (
        <Typography color="text.secondary">
          {groups.length === 0
            ? t('scanFindings.empty', 'No issues were found in this scan run.')
            : t('scanFindings.noneMatch', 'No issues match these filters.')}
        </Typography>
      )}

      {shown.map((category, i) => {
        const open = openCategories.has(category.category);
        const ids = categoryIds(i);
        return (
          <Box key={category.category} sx={{ mb: 1 }}>
            <Typography component="h3" variant="h6" sx={{ fontSize: '1.05rem', m: 0 }}>
              <Button
                id={ids.heading}
                aria-expanded={open}
                aria-controls={ids.panel}
                onClick={() => toggleCategory(category.category)}
                startIcon={<ExpandMoreIcon sx={groupIconSx(open)} aria-hidden />}
                color="inherit"
                sx={groupButtonSx}
              >
                {t('scanFindings.groups.category', '{{label}}, {{issues}} across {{rules}}', {
                  label: contentCategoryLabel(t, category.category),
                  issues: t('scanFindings.groups.issues', {
                    count: category.findings,
                    defaultValue_one: '{{count}} issue',
                    defaultValue_other: '{{count}} issues',
                  }),
                  rules: t('scanFindings.groups.rules', {
                    count: category.rules.length,
                    defaultValue_one: '{{count}} rule',
                    defaultValue_other: '{{count}} rules',
                  }),
                })}
              </Button>
            </Typography>
            <Box id={ids.panel} hidden={!open} sx={{ pl: { xs: 1, sm: 3 } }}>
              {open &&
                category.rules.map((rule, j) => {
                  const rOpen = openRules.has(rule.ruleId);
                  const r = ruleIds(i, j);
                  return (
                    <Box key={rule.ruleId} sx={{ mb: 1 }}>
                      <Typography component="h4" variant="subtitle1" sx={{ m: 0 }}>
                        <Button
                          id={r.heading}
                          aria-expanded={rOpen}
                          aria-controls={r.panel}
                          onClick={() => toggleRule(rule.ruleId)}
                          startIcon={<ExpandMoreIcon sx={groupIconSx(rOpen)} aria-hidden />}
                          color="inherit"
                          sx={groupButtonSx}
                        >
                          {/* REV-1163: the title's code spans render as <code>, in
                              whatever place the translation puts the label. */}
                          <RuleTitleInTemplate
                            template={t('scanFindings.groups.rule', '{{label}}, {{issues}}', {
                              label: RULE_SLOT,
                              issues: t('scanFindings.groups.issues', {
                                count: rule.findings,
                                defaultValue_one: '{{count}} issue',
                                defaultValue_other: '{{count}} issues',
                              }),
                            })}
                            slot={RULE_SLOT}
                            title={ruleLabel(rule)}
                          />
                        </Button>
                      </Typography>
                      <Box id={r.panel} hidden={!rOpen} sx={{ pl: { xs: 1, sm: 3 }, pb: 2 }}>
                        {rOpen && (
                          <>
                            <RuleSummary rule={rule} />
                            <RuleInstances
                              // Remounts on a new severity, so the instances
                              // start over at page one in one request.
                              key={`${rule.ruleId}:${severityFilter}`}
                              scanRunId={scanRunId}
                              ruleId={rule.ruleId}
                              severity={severityFilter || undefined}
                              usesEndpoint={usesEndpoint}
                              organizationId={organizationId}
                              canTriage={canTriage}
                              orgSlug={orgSlug}
                              ephemeralRows={ephemeral.rows}
                              messages={ephemeral.messages}
                              labelledBy={r.heading}
                              regionLabel={t('scanFindings.groups.instancesLabel', {
                                rule: stripCodeSpans(ruleLabel(rule)),
                                defaultValue: 'Instances of {{rule}}',
                              })}
                            />
                          </>
                        )}
                      </Box>
                    </Box>
                  );
                })}
            </Box>
          </Box>
        );
      })}
    </Paper>
  );
}

/**
 * What a rule's header shows ONCE, rather than on every instance: its WCAG
 * criteria, severities and remediation. Plain `pre-wrap` text, as finding
 * detail renders remediation.
 */
function RuleSummary({ rule }: { rule: RuleGroup }) {
  const { t } = useTranslation();
  const severities = (Object.entries(rule.bySeverity) as [string, number][])
    .filter(([, n]) => n > 0)
    .map(([sev, n]) =>
      t('scanFindings.groups.severityCount', {
        severity: t(`findings.severities.${sev}`, sev.charAt(0).toUpperCase() + sev.slice(1)),
        count: n,
        defaultValue: '{{severity}}: {{count}}',
      })
    );
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="body2" color="text.secondary">
        {t('scanFindings.groups.wcag', 'WCAG')}:{' '}
        {rule.wcagCriteria.length
          ? rule.wcagCriteria.join(', ')
          : t('scanFindings.groups.noCriteria', 'none mapped')}
        {' · '}
        {t('scanFindings.groups.severity', 'Severity')}: {severities.join(', ')}
      </Typography>
      {rule.remediation && (
        <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
          {rule.remediation}
        </Typography>
      )}
    </Box>
  );
}

const groupButtonSx = {
  justifyContent: 'flex-start',
  textAlign: 'left',
  textTransform: 'none',
  fontSize: 'inherit',
  fontWeight: 600,
  minHeight: 44,
  width: '100%',
} as const;

const groupIconSx = (open: boolean) => ({
  transform: open ? 'rotate(0deg)' : 'rotate(-90deg)',
  '@media (prefers-reduced-motion: no-preference)': { transition: 'transform 150ms' },
});
